import { useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLocalStorage } from '@mantine/hooks';

export type QueryParameters = Record<string, string>;

export function useSearchParameters(
  queryKey: string,
  checkStorage = false,
): {
  parameters: QueryParameters;
  setParameters: (parameters: QueryParameters) => void;
  resetParameters: () => void;
} {
  const navigate = useNavigate();
  const { search } = useLocation();
  const searchParameters = Object.fromEntries(new URLSearchParams(search));

  const [storedParameters, setStoredParameters] = useLocalStorage<QueryParameters>({
    key: queryKey,
    defaultValue: {},
  });

  const setParameters = useCallback(
    (newParameters: QueryParameters) => {
      const filteredParameters = Object.fromEntries(
        Object.entries(newParameters).filter(([, value]) => value != null && value !== ''),
      );
      const cleanedSearchParams = new URLSearchParams(filteredParameters).toString();
      navigate(`?${cleanedSearchParams}`, { replace: true });
      setStoredParameters(filteredParameters);
    },
    [navigate, setStoredParameters],
  );

  const resetParameters = useCallback(() => {
    navigate('', { replace: true });
    setStoredParameters({});
  }, [navigate, setStoredParameters]);

  // This will garantee that the filters are loaded correctly when opening a specific page
  useEffect(() => {
    if (checkStorage && Object.keys(searchParameters).length === 0 && Object.keys(storedParameters).length > 0) {
      setParameters(storedParameters);
    }
  }, [checkStorage, searchParameters, storedParameters, setParameters, queryKey]);

  return {
    parameters: searchParameters,
    setParameters,
    resetParameters,
  };
}
